import {
  ReviewMode,
  ReviewerStatus,
  SubmissionStatus,
  SubmissionVirtualEventsQuery,
} from "generated-graphql/graphql";

type Submission = NonNullable<
  SubmissionVirtualEventsQuery["submissionVirtualEvents"]["items"][0]["submission"]
>;
export type Reviewer = Submission["reviewers"][number];

/**
 * Determines if a reviewer has completed their review.
 * A reviewer is considered complete if any of their submissionDocumentReviews has a completedAt date.
 *
 * NOTE for humans and AI: The `submissionVirtualEvents` that provides the data for this component
 * only sends the most recent `SubmissionDocmentReview` record for each reviewer. So if the submission
 * has had mulitple rounds the review the table will correctly show the most recent SDR record results.
 */
export function hasCompletedReview(reviewer: Reviewer): boolean {
  return reviewer.submissionDocumentReviews.some(
    (review) => review.completedAt !== null
  );
}

/**
 * Determines if a reviewer's most recent action was a rejection.
 */
export function hasRejectedReview(reviewer: Reviewer): boolean {
  return reviewer.submissionDocumentReviews.some(
    (review) => review.reviewerStatus === ReviewerStatus.Rejected
  );
}

/**
 * Checks if all non-signer reviewers have completed their reviews.
 */
export function allNonSignersCompleted(reviewers: Reviewer[]): boolean {
  const nonSigners = reviewers.filter((r) => !r.isSigner);
  return nonSigners.every(hasCompletedReview);
}

/**
 * Gets the status label for a reviewer for the tooltip.
 * For signers, this now checks submission status to match the backend
 * computeWaitingOnUsers logic used by the "ready for next action" filter.
 */
export function getReviewerStatusLabel(
  reviewer: Reviewer,
  allReviewers: Reviewer[],
  reviewMode?: ReviewMode | null,
  submissionStatus?: SubmissionStatus | null
): string {
  const DONE = "Done";
  const REJECTED = "Rejected";
  const NOT_READY_FOR_ACTION = "Not ready for action";
  const READY_FOR_ACTION = "Ready for action";

  if (hasRejectedReview(reviewer)) {
    return REJECTED;
  }

  if (hasCompletedReview(reviewer)) {
    return DONE;
  }

  if (reviewer.isSigner && submissionStatus !== SubmissionStatus.InReview) {
    return NOT_READY_FOR_ACTION;
  }

  if (reviewer.isSigner && !allNonSignersCompleted(allReviewers)) {
    return NOT_READY_FOR_ACTION;
  }

  if (reviewMode !== ReviewMode.Sequential) {
    return READY_FOR_ACTION;
  }

  const reviewerOrder = reviewer.reviewOrder ?? 0;
  const precedingReviewers = allReviewers.filter(
    (r) => (r.reviewOrder ?? 0) < reviewerOrder
  );
  const allPrecedingComplete = precedingReviewers.every(hasCompletedReview);

  if (allPrecedingComplete) {
    return READY_FOR_ACTION;
  }

  return NOT_READY_FOR_ACTION;
}
